import React from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Calendar,
  Eye,
  Download,
  Mail,
  ShoppingCart,
  RefreshCw,
  Share2,
  Users,
  Target,
  Clock,
  MoreVertical,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { UnifiedRecipeCard } from "./recipe/UnifiedRecipeCard";
import { cn } from "./ui/utils";
import type { Recipe } from "../../types/recipe";
import { toast } from "sonner";

/**
 * MealPlanCard Component - Improved readability and visual hierarchy
 *
 * Design improvements:
 * 1. 4-tier visual hierarchy (status → name → recipe preview → metrics)
 * 2. Color-coded status backgrounds (coral/sage/stone)
 * 3. Horizontally scrollable recipe preview strip
 * 4. Simplified actions (primary button + dropdown menu)
 * 5. Responsive mobile/desktop layout
 */

// ============================================================================
// Types & Interfaces
// ============================================================================

interface MealPlan {
  id: string;
  name: string;
  weekRange: string;
  createdAt: string;
  recipeCount: number;
  servings: number;
  avgCalories: number;
  status: "completed" | "in-progress" | "archived";
  thumbnail: string;
}

interface PendingActions {
  download?: boolean;
  email?: boolean;
  regenerate?: boolean;
}

interface MealPlanCardProps {
  plan: MealPlan;
  recipes?: Recipe[];
  pending?: PendingActions;
  onViewPlan: (planId: string, planName: string) => void;
  onDownloadPdf: (planId: string, planName: string) => void;
  onEmailPlan: (planId: string, planName: string) => void;
  onViewShoppingList: (planId: string, planName: string) => void;
  onRegeneratePlan: (planId: string, planName: string) => void;
  onViewRecipe?: (recipeId: string) => void;
  disableRegenerate?: boolean;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get status color classes using design system tokens
 */
const getStatusColor = (status: MealPlan["status"]): string => {
  switch (status) {
    case "completed":
      return "bg-sage/10 text-sage-800 border-sage-200";
    case "in-progress":
      return "bg-coral/10 text-coral-800 border-coral-200";
    case "archived":
      return "bg-stone/10 text-martinique border-stone/20";
    default:
      return "bg-lynx text-martinique border-stone/20";
  }
};

/**
 * Get status background color for card
 */
const getStatusBgColor = (status: MealPlan["status"]): string => {
  switch (status) {
    case "completed":
      return "bg-sage/5 border-sage/20";
    case "in-progress":
      return "bg-coral/5 border-coral/20";
    case "archived":
      return "bg-stone/5 border-stone/20";
    default:
      return "bg-white border-stone/20";
  }
};

/**
 * Get status label
 */
const getStatusLabel = (status: MealPlan["status"]): string => {
  switch (status) {
    case "completed":
      return "Completed";
    case "in-progress":
      return "Active";
    case "archived":
      return "Archived";
    default:
      return status;
  }
};

// ============================================================================
// Main Component
// ============================================================================

export function MealPlanCard({
  plan,
  recipes = [],
  pending = {},
  onViewPlan,
  onDownloadPdf,
  onEmailPlan,
  onViewShoppingList,
  onRegeneratePlan,
  onViewRecipe,
  disableRegenerate,
}: MealPlanCardProps) {
  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleViewPlan = () => {
    onViewPlan(plan.id, plan.name);
  };

  const handleDownloadPdf = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownloadPdf(plan.id, plan.name);
  };

  const handleEmailPlan = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEmailPlan(plan.id, plan.name);
  };

  const handleViewShoppingList = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewShoppingList(plan.id, plan.name);
  };

  const handleRegeneratePlan = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRegeneratePlan(plan.id, plan.name);
  };

  const handleSharePlan = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const url = `${window.location.origin}/plans/${plan.id}`;
      if ((navigator as any).share) {
        try {
          await (navigator as any).share({ title: plan.name, url });
          return;
        } catch {}
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch (err) {
      console.error("Share failed", err);
      toast.error("Couldn't share link");
    }
  };

  const handleRecipeClick = (recipeId: string) => {
    onViewRecipe?.(recipeId);
  };

  // Show up to 6 recipes in preview
  const previewRecipes = recipes.slice(0, 6);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Card
      className={cn(
        "rounded-[var(--radius-card)] border transition-all duration-normal",
        "shadow-cardShadow desktop-hover:shadow-cardHover",
        getStatusBgColor(plan.status)
      )}
    >
      <CardContent className="p-6">
        {/* ====================================================================
            TIER 1: Status & Week Range (Subtle header)
        ==================================================================== */}
        <div className="mb-3 flex items-center justify-between">
          <Badge
            variant="outline"
            className={cn("text-xs font-medium", getStatusColor(plan.status))}
          >
            {getStatusLabel(plan.status)} • {plan.weekRange}
          </Badge>
          <span className="text-xs text-martinique/40">
            {new Date(plan.createdAt).toLocaleDateString()}
          </span>
        </div>

        {/* ====================================================================
            TIER 2: Plan Name & Emoji (Primary content)
        ==================================================================== */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-coral/10">
            <span className="text-2xl">{plan.thumbnail}</span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-lg font-semibold leading-tight text-dusk">
              {plan.name}
            </h3>
            <p className="text-xs text-martinique/60">
              {plan.recipeCount} recipes • {plan.servings}{" "}
              {plan.servings === 1 ? "serving" : "servings"}
            </p>
          </div>
        </div>

        {/* ====================================================================
            TIER 3: Recipe Preview Strip (Visual proof)
        ==================================================================== */}
        {previewRecipes.length > 0 && (
          <div className="mb-4">
            <div className="-mx-6 overflow-x-auto px-6 scrollbar-hide">
              <div className="flex gap-3 pb-2">
                {previewRecipes.map((recipe) => (
                  <div
                    key={recipe.id}
                    className="w-[140px] flex-shrink-0 sm:w-[160px]"
                  >
                    <UnifiedRecipeCard
                      recipe={recipe}
                      variant="compact"
                      size="sm"
                      features={{
                        like: false,
                        addToPlan: false,
                        rating: false,
                        difficulty: true,
                        tags: false,
                      }}
                      onViewDetails={handleRecipeClick}
                      imageFetchPriority="auto"
                      showDescription={false}
                      reserveDescription={false}
                    />
                  </div>
                ))}
                {recipes.length > previewRecipes.length && (
                  <div className="flex w-[100px] flex-shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-stone/30 bg-cream/50">
                    <div className="text-center">
                      <p className="text-sm font-semibold text-martinique">
                        +{recipes.length - previewRecipes.length}
                      </p>
                      <p className="text-xs text-martinique/60">more</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ====================================================================
            TIER 4: Metrics Grid (Supporting info)
        ==================================================================== */}
        <div className="mb-4 grid grid-cols-3 gap-3 rounded-lg bg-cream/50 p-3">
          <div className="text-center">
            <div className="mb-0.5 flex items-center justify-center gap-1 text-sm font-semibold text-coral">
              <Target className="h-3.5 w-3.5" />
              {plan.avgCalories}
            </div>
            <div className="text-xs text-martinique/60">cal/day</div>
          </div>
          <div className="text-center">
            <div className="mb-0.5 flex items-center justify-center gap-1 text-sm font-semibold text-sage-700">
              <Calendar className="h-3.5 w-3.5" />
              {plan.recipeCount}
            </div>
            <div className="text-xs text-martinique/60">recipes</div>
          </div>
          <div className="text-center">
            <div className="mb-0.5 flex items-center justify-center gap-1 text-sm font-semibold text-coral">
              <Users className="h-3.5 w-3.5" />
              {plan.servings}x
            </div>
            <div className="text-xs text-martinique/60">servings</div>
          </div>
        </div>

        {/* ====================================================================
            Actions: Primary button + Dropdown menu
        ==================================================================== */}
        <div className="flex gap-2">
          {/* Primary Action */}
          <Button
            onClick={handleViewPlan}
            className="flex-1 bg-coral font-medium text-white transition-all desktop-hover:bg-coral-600 desktop-hover:scale-[1.02]"
          >
            <Eye className="mr-2 h-4 w-4" />
            View Plan
          </Button>

          {/* Secondary Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="border-stone bg-card text-card-foreground desktop-hover:bg-stone/20"
                aria-label="More actions"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={handleDownloadPdf}
                disabled={pending.download}
                className="cursor-pointer"
              >
                {pending.download ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download PDF
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={handleEmailPlan}
                disabled={pending.email}
                className="cursor-pointer"
              >
                {pending.email ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="mr-2 h-4 w-4" />
                )}
                Email Plan
              </DropdownMenuItem>

              <DropdownMenuItem onClick={handleSharePlan} className="cursor-pointer">
                <Share2 className="mr-2 h-4 w-4" />
                Share Link
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={handleViewShoppingList}
                className="cursor-pointer"
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                Shopping List
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={handleRegeneratePlan}
                disabled={pending.regenerate || !!disableRegenerate}
                className="cursor-pointer"
              >
                {pending.regenerate ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : disableRegenerate ? (
                  <RefreshCw className="mr-2 h-4 w-4 opacity-50" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Regenerate Plan
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}

export default MealPlanCard;
